import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface Project {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  logoUrl: string | null;
  config: Record<string, unknown>;
  defaultPipelineId: string | null;
  createdAt: string;
  archivedAt: string | null;
  defaultPipeline?: {
    id: string;
    name: string;
  };
  _count?: {
    leads: number;
  };
}

export interface CreateProjectDto {
  name: string;
  slug?: string;
  description?: string;
}

export interface UpdateProjectDto {
  name?: string;
  description?: string;
  color?: string;
  defaultPipelineId?: string;
}

export function useProjects() {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['projects', currentTenant?.id],
    queryFn: () => api.get<Project[]>('/projects'),
    enabled: !!currentTenant,
  });
}

export function useProject(id: string) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['project', id, currentTenant?.id],
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectDto) => api.post<Project>('/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectDto }) =>
      api.patch<Project>(`/projects/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
